import { isTelling } from './format';
import { effectieveDrempel } from './drempel';
import type { VeldUitleg, EigenWekenoverzicht, WerksoortSleutel } from './work-quality.api';

/**
 * De regels die de leerkaart en het blokje "komt dit terug" volgen (ux-spec 3.3 en 3.4).
 *
 * Geen rekenwerk op de meting: de verhouding in de trend wordt alleen vergeleken, nooit als getal
 * getoond, en de drempel wordt niet berekend maar gelezen uit het antwoord.
 */

/**
 * Of een aantal onder de drempel valt, en dus geen cijfer mag worden.
 *
 * Ontbreekt de drempel of het aantal, dan geldt het als onder de drempel. Liever geen cijfer dan een
 * cijfer dat niet mag (ux-spec beslissing 1).
 */
export function onderDrempel(aantal: unknown, drempel: unknown): boolean {
  if (!isTelling(aantal) || !isTelling(drempel)) return true;
  return aantal < drempel;
}

/** Een tekst uit de uitleg, of `null` als hij ontbreekt of leeg is. Nooit een lege tekst op het scherm. */
function tekst(waarde: unknown): string | null {
  return typeof waarde === 'string' && waarde.trim() !== '' ? waarde : null;
}

export interface UitlegTeksten {
  watGaatMis: string | null;
  waarom: string | null;
  zoMoetHet: string | null;
}

/** De drie uitlegteksten van een veld, of `null` per tekst die ontbreekt. */
export function uitlegVan(veld: { uitleg?: VeldUitleg | null }): UitlegTeksten {
  const uitleg = veld.uitleg ?? null;
  return {
    watGaatMis: tekst(uitleg?.watGaatMis),
    waarom: tekst(uitleg?.waarom),
    zoMoetHet: tekst(uitleg?.zoMoetHet),
  };
}

/**
 * Eén van de vier vaste plekken van het blokje "komt dit terug" (`trend-en-maand.md`, sectie Trend).
 * Alleen een `staaf` draagt een getal; de andere drie zeggen waarom er geen is.
 */
export type TrendPlek =
  | { weekKey: string; soort: 'staaf'; aantal: number; totaal: number }
  | { weekKey: string; soort: 'geenGegevens' | 'teWeinigProducten' | 'teWeinigGegevens' };

/** Wat er op één plek staat, voor één veld in één werksoort van één week. */
function plekVan(
  overzicht: EigenWekenoverzicht,
  weekKey: string,
  soort: WerksoortSleutel,
  veldpad: string,
): TrendPlek {
  const leeg: TrendPlek = { weekKey, soort: 'geenGegevens' };
  const week = overzicht.weken.find((w) => w.weekKey === weekKey);
  // Week zonder document, mislukt, of een vorm die niet klopt: geen gegevens (criteria 33 en 42).
  if (week === undefined || week.reden !== undefined) return leeg;
  const werksoort = week.werksoorten?.[soort];
  if (werksoort === undefined) return leeg;
  if (werksoort.reden === 'te_weinig_gegevens') return { weekKey, soort: 'teWeinigProducten' };
  if (werksoort.reden !== undefined) return leeg;

  const totaal = werksoort.aantalBeoordeeldeProducten;
  if (!isTelling(totaal) || !Number.isInteger(totaal)) return leeg;
  // Nooit een cijfer onder max(20, drempel van de dienst), ook als de dienst er een stuurt.
  if (onderDrempel(totaal, effectieveDrempel(overzicht.drempelProductenPerWerksoort))) {
    return { weekKey, soort: 'teWeinigProducten' };
  }
  if (!Array.isArray(werksoort.velden)) return leeg;

  const veld = werksoort.velden.find((v) => v.veld === veldpad);
  // Een veld dat ontbreekt in een werksoort met cijfers had 0 producten met een correctie.
  if (veld === undefined) return { weekKey, soort: 'staaf', aantal: 0, totaal };
  if (veld.reden === 'te_weinig_gegevens') return { weekKey, soort: 'teWeinigGegevens' };
  // In bespreking, of een reden die het portaal niet kent: er staat niets van (besluit 29-10-2026 #2).
  if (veld.reden !== undefined) return leeg;

  const aantal = veld.aantalProductenMetCorrectie;
  if (!isTelling(aantal) || !Number.isInteger(aantal) || aantal > totaal) return leeg;
  // 1 tot de veldgrens: gestreept zonder getal, ook als de dienst het toch stuurt (besluit 29-10-2026 #1).
  if (aantal >= 1 && onderDrempel(aantal, overzicht.drempelProductenPerVeld)) {
    return { weekKey, soort: 'teWeinigGegevens' };
  }
  return { weekKey, soort: 'staaf', aantal, totaal };
}

/**
 * De vier plekken van het blokje, oudste links, uit de gevraagde weeksleutels en niet uit het antwoord:
 * een week die in het antwoord ontbreekt blijft een lege plek op zijn eigen plaats.
 */
export function trendPlekken(
  overzicht: EigenWekenoverzicht,
  weken: readonly string[],
  soort: WerksoortSleutel,
  veld: string,
): TrendPlek[] {
  return weken.map((weekKey) => plekVan(overzicht, weekKey, soort, veld));
}

/** Grijs betekent alleen "gelijk"; gestreept is "geen vergelijking mogelijk" (besluit 28-10 #2 en #5). */
export type TrendKleur = 'gestreept' | 'grijs' | 'oranje' | 'groen';

/**
 * De kleur van één staaf: vergelijk de verhouding met de plek ervoor (ux-spec 3.4, criteria 5, 32, 48).
 *
 * Gestreept als er geen plek ervoor is of die plek geen staaf met "X van Y" is. Verhoudingen worden
 * kruislings vermenigvuldigd in plaats van gedeeld, zodat er geen afrondingsfout in komt: `a/b < c/d`
 * is `a*d < c*b` als b en d positief zijn.
 */
export function trendKleur(vorige: TrendPlek | null, huidige: Extract<TrendPlek, { soort: 'staaf' }>): TrendKleur {
  if (vorige === null || vorige.soort !== 'staaf') return 'gestreept';
  const huidigeMaalVorige = huidige.aantal * vorige.totaal;
  const vorigeMaalHuidige = vorige.aantal * huidige.totaal;
  if (huidigeMaalVorige > vorigeMaalHuidige) return 'oranje';
  if (huidigeMaalVorige < vorigeMaalHuidige) return 'groen';
  return 'grijs';
}

/**
 * De breedte en de hoogte als klasse uit een vaste tabel, in stappen van vijf procent (ux-spec 3.2 en
 * 3.4). Tailwind heeft de klassetekst letterlijk in de bron nodig: een `w-[${n}%]` bestaat na het bouwen
 * niet. Een inline stijl mag ook niet (lintregel). Vijf procent is 12 pixels; 25 procent is 60 pixels, zoals
 * het ontwerp vraagt.
 */
export const BREEDTE_KLASSE: Record<number, string> = {
  0: 'w-[0%]',
  5: 'w-[5%]',
  10: 'w-[10%]',
  15: 'w-[15%]',
  20: 'w-[20%]',
  25: 'w-[25%]',
  30: 'w-[30%]',
  35: 'w-[35%]',
  40: 'w-[40%]',
  45: 'w-[45%]',
  50: 'w-[50%]',
  55: 'w-[55%]',
  60: 'w-[60%]',
  65: 'w-[65%]',
  70: 'w-[70%]',
  75: 'w-[75%]',
  80: 'w-[80%]',
  85: 'w-[85%]',
  90: 'w-[90%]',
  95: 'w-[95%]',
  100: 'w-[100%]',
};

export const HOOGTE_KLASSE: Record<number, string> = {
  0: 'h-[0px]',
  5: 'h-[12px]',
  10: 'h-[24px]',
  15: 'h-[36px]',
  20: 'h-[48px]',
  25: 'h-[60px]',
  30: 'h-[72px]',
  35: 'h-[84px]',
  40: 'h-[96px]',
  45: 'h-[108px]',
  50: 'h-[120px]',
  55: 'h-[132px]',
  60: 'h-[144px]',
  65: 'h-[156px]',
  70: 'h-[168px]',
  75: 'h-[180px]',
  80: 'h-[192px]',
  85: 'h-[204px]',
  90: 'h-[216px]',
  95: 'h-[228px]',
  100: 'h-[240px]',
};

/** Een procent naar de dichtstbijzijnde stap van vijf, tussen 0 en 100. Alleen tekening, geen getal. */
export function procentStap(procent: number): number {
  if (!Number.isFinite(procent)) return 0;
  return Math.min(100, Math.max(0, Math.round(procent / 5) * 5));
}

/**
 * Een naam zonder spatie (een veldpad of een ononderbroken woord) past niet naast de rest van de kaart en
 * duwt de pagina breder dan het scherm. Die krijgt afkapping in plaats van een horizontale scroll; de
 * naam staat dan nog steeds in het adres van de kaart. Een gewone zin breekt gewoon op de spaties.
 */
export const BREEK_ONONDERBROKEN_NAAM = 'truncate min-w-0';
